import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../Contexts/APIContext";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import CallFiltersViewModel from "./ViewModels/CallFiltersViewModel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import { handleAutoCompleteChange } from "../Utility/FilterChangeHandlers";
import { TextFieldValidator } from "../Utility/InputValidation";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

interface ICallFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

const timeList: string[] = ['No Min', '5 sec', '10 sec', '15 sec', '20 sec', '25 sec', '30 sec', '1 min', '2 min', '3 min', '4 min', '5 min',
    '10 min', '15 min', '20 min', '25 min', '30 min', '35 min', '40 min', '45 min', '50 min', '55 min', '60 min', 'No Max']

export default function CallFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: ICallFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [results, setResults] = useState<string[]>([]);
    const [resultSources, setResultSources] = useState<string[]>([]);
    const [startTime, setStartTime] = useState<string>(timeList[0]);
    const [endTime, setEndTime] = useState<string>(timeList[timeList.length - 1]);
    const [callFilters, setCallFilters] = useState<CallFiltersViewModel>(new CallFiltersViewModel());

    useEffect(() => {
        if (open) {
            setCallFilters(callTableViewModel.filters.callFilters)
            setStartTime(modelToLocalTime(callTableViewModel.filters.callFilters.callLengthMin));
            setEndTime(modelToLocalTime(callTableViewModel.filters.callFilters.callLengthMax));
        }
    }, [open])

    useEffect(() => {
        setCallFilters({
            ...callFilters,
            callLengthMin: timeToSeconds(startTime)
        })
    }, [startTime])

    useEffect(() => {
        setCallFilters({
            ...callFilters,
            callLengthMax: timeToSeconds(endTime)
        })
    }, [endTime])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('search', `GetResultDropDown?client=${callTableViewModel.filters.client}`, (data) => setResults(data));
            get('search', `GetEventIDDropDown`, (data) => setResultSources(data));
        }
    }, [callTableViewModel.filters.client])

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const inputProps: InputProps[] = [
        {
            name: 'result',
            label: 'Result',
            value: callFilters.result,
            options: results,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'resultSource',
            label: 'Result Source',
            value: callFilters.resultSource,
            options: resultSources,
            type: InputType.AutoComplete
        },
        {
            name: 'systemID',
            label: 'System ID',
            value: callFilters.systemID,
            type: InputType.TextField
        },
    ];

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                <InputLabel>{inputProps.label}</InputLabel>
                {
                    inputProps.type === InputType.TextField &&
                    <TextField
                        name={inputProps.name}
                        onChange={handleChange}
                        value={inputProps.value}
                        fullWidth
                    />
                }
                {
                    inputProps.type === InputType.AutoComplete &&
                    <>
                        <Autocomplete
                            onChange={(_e: any, newValue: string) => handleAutoCompleteChange(inputProps.name, newValue, setCallFilters)}
                            options={inputProps.options!}
                            value={inputProps.value as string}
                            fullWidth
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </>
                }
                {
                    inputProps.type === InputType.AutoCompleteMultiple &&
                    <Autocomplete
                        multiple
                        fullWidth
                        options={inputProps.options!}
                        disableCloseOnSelect
                        value={inputProps.value as string[]}
                        getOptionLabel={(option) => option}
                        onChange={(event: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setCallFilters) }}
                        limitTags={3}
                        renderOption={(props, option, { selected }) => {
                            const { ...optionProps } = props;
                            return (
                                <li {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth />
                        )}
                        renderTags={(value, getTagProps) => {
                            const numTags = value.length;
                            const limitTags = 1;
                            return (
                                <>
                                    {value.slice(0, limitTags).map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={index}
                                            label={option}
                                        />
                                    ))}

                                    {numTags > limitTags && ` +${numTags - limitTags}`}
                                </>
                            );
                        }}
                    />
                }
            </Grid>)
    }

    const handleChange = (e: any) => {
        if (TextFieldValidator(e.target.value))
            setCallFilters({
                ...callFilters,
                [e.target.name]: e.target.value
            })
    }

    const handleChangeInput = (name: string, value: any) => {
        setCallFilters({
            ...callFilters,
            [name]: value
        })
    }

    const timeToSeconds = (value: string): number => {
        let secondsCounter: number = 0;

        if (value === timeList[0]) { //convert no min to 0
            secondsCounter = 0;
        }
        else if (value === timeList[timeList.length - 1]) { //convert no max to 61
            secondsCounter = 61;
        }
        else {
            let intValue: number = +value.substring(0, value.length - 4);//length-4 as ' sec' and ' min' are 4 chars

            if (value.includes('sec')) //if the list value is seconds 
                return intValue //don't need to multiply by 60 as not a min so leave early
            else //value in mins
                secondsCounter = +intValue;

        }

        return secondsCounter * 60; //3600 max, 0 min
    }

    const modelToLocalTime = (value: number): string => {
        let secondsCounter: string;

        if (value / 60 === 0) {
            secondsCounter = timeList[0];
        }
        else if (value / 60 === 61) {
            secondsCounter = timeList[timeList.length - 1];
        }
        else {

            if (value < 60) //if the value is in seconds
                secondsCounter = String(value) + ' sec';
            else
                secondsCounter = String(value / 60) + ' min';
        }

        return secondsCounter;
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.callFilters = callFilters;

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    return (
        <Dialog onClose={() => setOpen(false)} open={open} fullWidth maxWidth="sm" >
            <DialogTitle>Call Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    <Grid size={6} >
                        <FormControlLabel control={<Checkbox checked={callFilters.oppToSell} onChange={() => handleChangeInput("oppToSell", !callFilters.oppToSell)} />} label="Opportunity To Sell" />
                    </Grid>
                    <Grid size={6} >
                        <FormControlLabel control={<Checkbox checked={callFilters.notOppToSell} onChange={() => handleChangeInput("notOppToSell", !callFilters.notOppToSell)} />} label="Not Opportunity To Sell" />
                    </Grid>
                    <Grid size={12} >
                        <FormControlLabel control={<Checkbox checked={callFilters.resultExclude} onChange={() => handleChangeInput("resultExclude", !callFilters.resultExclude)} />} label="Result Exclude" />
                    </Grid>
                    {
                        inputProps.map(props => getInput(props))
                    }
                    <Grid size={12} >
                        <FormControlLabel control={<Checkbox checked={callFilters.isCallLengthFlag} onChange={() => handleChangeInput("isCallLengthFlag", !callFilters.isCallLengthFlag)} />} label="Call Length" />
                    </Grid>
                    <Grid container size={12}>
                        <Grid size={6} >
                            <Select
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value as string)}
                                disabled={!callFilters.isCallLengthFlag}
                                fullWidth>
                                {
                                    timeList.slice(0, timeList.indexOf(endTime) + 1).map(item =>
                                        <MenuItem key={item} value={item}>{item}</MenuItem>)
                                }
                            </Select>
                        </Grid>
                        <Grid size={6} >
                            <Select
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value as string)}
                                disabled={!callFilters.isCallLengthFlag}
                                fullWidth>
                                {
                                    timeList.slice(timeList.indexOf(startTime), timeList.length).map(item =>
                                        <MenuItem key={item} value={item}>{item}</MenuItem>)
                                }
                            </Select>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}
